import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Plus, Flag, Trophy, Server, Check, Link as LinkIcon, AlertTriangle, Play,
    Loader2, Save, FileEdit, Crosshair, ChevronRight, ListChecks, X, Users, Globe, ArrowRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './TeamSetupWizard.css';
import { TeamRosterWizard } from './TeamRosterWizard';
import { TeamTelegramWizard } from './TeamTelegramWizard';
import { TeamMapPriorityWizard } from './TeamMapPriorityWizard';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const SERVER_LOCATIONS = [
    { id: 'europe-west', flag: '🇩🇪', city: 'Frankfurt', region: 'EU West', cc: 'de' },
    { id: 'europe-north', flag: '🇸🇪', city: 'Stockholm', region: 'EU North', cc: 'se' },
    { id: 'europe-east', flag: '🇵🇱', city: 'Warsaw', region: 'EU East', cc: 'pl' },
    { id: 'europe-france', flag: '🇫🇷', city: 'Paris', region: 'EU West', cc: 'fr' },
    { id: 'europe-uk', flag: '🇬🇧', city: 'London', region: 'EU West', cc: 'gb' },
    { id: 'middle-east', flag: '🇦🇪', city: 'Dubai', region: 'Middle East', cc: 'ae' },
    { id: 'north-america-east', flag: '🇺🇸', city: 'New York', region: 'NA East', cc: 'us' },
    { id: 'north-america-west', flag: '🇺🇸', city: 'Los Angeles', region: 'NA West', cc: 'us' },
];

interface TeamSetupWizardProps {
    teamId: string;
    onComplete: () => void;
}

export function TeamSetupWizard({ teamId, onComplete }: TeamSetupWizardProps) {
    const { t } = useTranslation();
    const [step, setStep] = useState<'league' | 'roster' | 'mapPriority' | 'telegram' | 'serverRegion' | 'finish'>('league');
    const [rosterToImport, setRosterToImport] = useState<any[]>([]);
    const [selectedServerLocation, setSelectedServerLocation] = useState('europe-west');
    const [wizardExiting, setWizardExiting] = useState(false);
    const [stepExiting, setStepExiting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (error) {
            const t = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(t);
        }
    }, [error]);

    const [faceitUrl, setFaceitUrl] = useState('');
    const [fetchedLeague, setFetchedLeague] = useState<{ name: string, avatar: string | null } | null>(null);

    type ImportItem = 
        | { type: 'faceit', url: string, name: string, avatar: string | null, autoDetected?: false, isClosed?: boolean, format?: string }
        | { type: 'esea', id: string, name: string, division: string, region: string, continent: string, faceitUrl: string | null, autoDetected: true, isClosed?: boolean, format?: string }
        | { type: 'dach', id: string, name: string, division: string, group: string | null, championshipUrl: string | null, avatar: string | null, autoDetected: true, isClosed?: boolean, format?: string };

    const [importQueue, setImportQueue] = useState<ImportItem[]>([]);
    const [removedQueue, setRemovedQueue] = useState<ImportItem[]>([]);
    const [showCompleted, setShowCompleted] = useState(false);
    const [archivedTab, setArchivedTab] = useState<'active' | 'history'>('active');

    // Auto-populate import queue from ESEA and DACH suggestions
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        const fetchSuggestions = async () => {
            const autoItems: ImportItem[] = [];

            try {
                const eseaRes = await axios.get(`${API}/leagues/esea-suggestions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (eseaRes.data && Array.isArray(eseaRes.data)) {
                    for (const league of eseaRes.data) {
                        autoItems.push({
                            type: 'esea',
                            id: league.id,
                            name: league.seasonName,
                            division: league.division,
                            region: league.region,
                            continent: league.continent,
                            faceitUrl: league.overviewUrl || null,
                            autoDetected: true,
                            isClosed: league.isClosed,
                            format: league.format
                        });
                    }
                }
            } catch (e) { console.error('Failed to fetch ESEA suggestions', e); }

            try {
                const dachRes = await axios.get(`${API}/leagues/dach-suggestions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (dachRes.data && Array.isArray(dachRes.data)) {
                    for (const league of dachRes.data) {
                        autoItems.push({
                            type: 'dach',
                            id: league.id,
                            name: league.seasonName,
                            division: league.division,
                            group: league.group || null,
                            championshipUrl: league.championshipUrl || null,
                            avatar: league.championshipAvatar || null,
                            autoDetected: true,
                            isClosed: league.isClosed,
                            format: league.format
                        });
                    }
                }
            } catch (e) { console.error('Failed to fetch DACH suggestions', e); }

            if (autoItems.length > 0) {
                const active = autoItems.filter(i => !i.isClosed);
                const closed = autoItems.filter(i => i.isClosed);
                setImportQueue(prev => [...active, ...prev]);
                setRemovedQueue(prev => [...closed, ...prev]);
            }
        };

        fetchSuggestions();
    }, []);

    const handleFetchFaceit = async () => {
        if (!faceitUrl.trim() || fetchedLeague) return;
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const infoRes = await axios.get(`${API}/faceit/league-info`, {
                params: { url: faceitUrl },
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (infoRes.data?.isParticipating === false) {
                throw new Error("Your team does not appear to be participating in this league.");
            }

            setFetchedLeague({
                name: infoRes.data?.name || 'Unknown League',
                avatar: infoRes.data?.avatar || null
            });
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to fetch league preview');
        } finally {
            setLoading(false);
        }
    };

    const handleClearFaceitFetch = () => {
        setFaceitUrl('');
        setFetchedLeague(null);
    };

    const handleAddFaceitToQueue = () => {
        if (!fetchedLeague) return;
        if (importQueue.find(q => q.type === 'faceit' && q.url === faceitUrl)) return;

        setImportQueue(prev => [...prev, {
            type: 'faceit',
            url: faceitUrl,
            name: fetchedLeague.name,
            avatar: fetchedLeague.avatar
        }]);
        setFaceitUrl('');
        setFetchedLeague(null);
    };

    const handleRemoveFromQueue = (index: number) => {
        const item = importQueue[index];
        if (!item) return;

        setImportQueue(prev => prev.filter((_, i) => i !== index));
        
        // If it's an auto-detected league, move it to removedQueue
        if (item.autoDetected) {
            setRemovedQueue(prev => [...prev, item]);
        }
    };

    const handleRestoreFromRemoved = (originalIndex: number) => {
        const item = removedQueue[originalIndex];
        if (!item) return;
        
        setRemovedQueue(prev => prev.filter((_, i) => i !== originalIndex));
        setImportQueue(prev => [...prev, item]);
    };

    const handleContinueToRoster = () => {
        setStepExiting(true);
        setTimeout(() => {
            setStep('roster');
            setStepExiting(false);
        }, 300);
    };

    const handleCompleteAll = async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            
            // 1. Import Leagues
            for (const item of importQueue) {
                if (item.type === 'faceit') {
                    const createRes = await axios.post(`${API}/leagues`, {
                        name: item.name,
                        faceitUrl: item.url,
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    
                    if (createRes.data?.id) {
                        try {
                            await axios.post(`${API}/leagues/${createRes.data.id}/sync`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                        } catch (syncErr) {
                            console.error('Failed to sync', item.name, syncErr);
                        }
                    }
                } else if (item.type === 'esea') {
                    const createRes = await axios.post(`${API}/leagues`, {
                        name: `${item.name} ${item.division}`,
                        eseaLeagueId: item.id,
                        faceitUrl: item.faceitUrl,
                        isClosed: item.isClosed,
                        format: item.format
                    }, { headers: { Authorization: `Bearer ${token}` } });

                    if (createRes.data?.id) {
                        try {
                            await axios.post(`${API}/leagues/${createRes.data.id}/sync`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                        } catch (syncErr) {
                            console.error('Failed to sync ESEA league', item.name, syncErr);
                        }
                    }
                } else if (item.type === 'dach') {
                    const createRes = await axios.post(`${API}/leagues`, {
                        name: `${item.name} ${item.division}${item.group ? ' ' + item.group : ''}`,
                        dachLeagueId: item.id,
                        faceitUrl: item.championshipUrl,
                        isClosed: item.isClosed,
                        format: item.format
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    
                    if (createRes.data?.id) {
                        try {
                            await axios.post(`${API}/leagues/${createRes.data.id}/sync`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                        } catch (syncErr) {
                            console.error('Failed to sync DACH league', item.name, syncErr);
                        }
                    }
                }
            }

            // 2. Import Ghost Players
            if (rosterToImport.length > 0) {
                await axios.post(`${API}/teams/${teamId}/ghost-roster`, {
                    players: rosterToImport
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            // 3. Save Server Location
            if (selectedServerLocation && selectedServerLocation !== 'europe-west') {
                await axios.patch(`${API}/teams/details`, {
                    serverLocation: selectedServerLocation
                }, { headers: { Authorization: `Bearer ${token}` } });
            }

            // 4. Mark setup complete
            await axios.patch(`${API}/teams/${teamId}/setup-complete`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setWizardExiting(true);
            setTimeout(() => {
                localStorage.setItem('showPremiumUpsell', 'true');
                onComplete();
                window.location.reload();
            }, 300);
        } catch (err: any) {
             setError(err.response?.data?.message || 'Failed to finalize setup');
             setLoading(false);
        }
    };

    // Remove the early returns and render everything inside the common overlay
    return (
        <div className={`team-onboarding fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm transition-opacity duration-300 ${wizardExiting ? 'opacity-0' : 'opacity-100'}`}>
            <div className={`transition-all duration-300 ${stepExiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                {step === 'roster' && (
                    <TeamRosterWizard teamId={teamId} initialRoster={rosterToImport} onComplete={(players) => {
                        setRosterToImport(players);
                        setStepExiting(true);
                        setTimeout(() => {
                            setStep('mapPriority');
                            setStepExiting(false);
                        }, 300);
                    }} onBack={() => {
                        setStepExiting(true);
                        setTimeout(() => {
                            setStep('league');
                            setStepExiting(false);
                        }, 300);
                    }} />
                )}

                {step === 'mapPriority' && (
                    <TeamMapPriorityWizard teamId={teamId} onComplete={() => {
                        setStepExiting(true);
                        setTimeout(() => {
                            setStep('telegram');
                            setStepExiting(false);
                        }, 300);
                    }} onBack={() => {
                        setStepExiting(true);
                        setTimeout(() => {
                            setStep('roster');
                            setStepExiting(false);
                        }, 300);
                    }} />
                )}

                {step === 'telegram' && (
                    <TeamTelegramWizard teamId={teamId} onComplete={() => {
                        setStepExiting(true);
                        setTimeout(() => {
                            setStep('serverRegion');
                            setStepExiting(false);
                        }, 300);
                    }} onBack={() => {
                        setStepExiting(true);
                        setTimeout(() => {
                            setStep('mapPriority');
                            setStepExiting(false);
                        }, 300);
                    }} />
                )}

                {step === 'serverRegion' && (
                    <div className="w-[600px] bg-slate-900 border border-slate-700/50 rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden">
                        <div className="p-8 text-center border-b border-slate-700/30">
                            <div className="w-16 h-16 bg-cyan-500/10 text-cyan-400 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                                <Globe size={32} />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">Server Region</h2>
                            <p className="text-sm text-slate-400">Choose where your game servers will be hosted. You can change this later on the Game Server page.</p>
                        </div>
                        <div className="p-8 bg-slate-800/20 flex flex-col items-center gap-3">
                            <div className="grid grid-cols-4 gap-2 w-full max-w-lg">
                            {SERVER_LOCATIONS.map(loc => (
                                <button
                                    key={loc.id}
                                    onClick={() => setSelectedServerLocation(loc.id)}
                                    className={`relative flex flex-col items-center justify-end gap-1 py-2.5 px-2 rounded-xl border-2 transition-all duration-200 cursor-pointer group overflow-hidden ${
                                        selectedServerLocation === loc.id
                                            ? 'border-cyan-500/50 shadow-lg shadow-cyan-500/10'
                                            : 'border-slate-700/40 hover:border-slate-500 hover:shadow-md'
                                    }`}
                                    style={{ minHeight: '80px' }}
                                >
                                    <div
                                        className={`absolute inset-0 bg-cover bg-center transition-opacity duration-300 ${
                                            selectedServerLocation === loc.id ? 'opacity-30' : 'opacity-15 group-hover:opacity-25'
                                        }`}
                                        style={{ backgroundImage: `url(/flags/${loc.cc}.png)` }}
                                    />
                                    <div className={`absolute inset-0 transition-colors duration-200 ${
                                        selectedServerLocation === loc.id
                                            ? 'bg-gradient-to-t from-cyan-950/80 via-slate-900/50 to-slate-900/30'
                                            : 'bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20'
                                    }`} />
                                    <div className="relative z-10 text-center">
                                        <div className={`font-bold text-[11px] leading-tight drop-shadow-sm ${selectedServerLocation === loc.id ? 'text-cyan-300' : 'text-white'}`}>{loc.city}</div>
                                        <div className="text-slate-400 text-[9px] font-semibold uppercase tracking-wider">{loc.region}</div>
                                    </div>
                                    {selectedServerLocation === loc.id && (
                                        <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full bg-cyan-500 flex items-center justify-center z-10">
                                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                        </div>
                                    )}
                                </button>
                            ))}
                            </div>
                        </div>
                        <div className="p-6 bg-slate-950 border-t border-slate-700/30 flex items-center justify-between">
                            <button 
                                className="px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                                onClick={() => {
                                    setStepExiting(true);
                                    setTimeout(() => {
                                        setStep('telegram');
                                        setStepExiting(false);
                                    }, 300);
                                }}
                            >
                                Go Back
                            </button>
                            <button 
                                className="bg-cyan-600 hover:bg-cyan-500 text-white px-8 py-2.5 rounded-lg flex items-center gap-2 font-bold shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all"
                                onClick={() => {
                                    setStepExiting(true);
                                    setTimeout(() => {
                                        setStep('finish');
                                        setStepExiting(false);
                                    }, 300);
                                }}
                            >
                                <ChevronRight size={16} />
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {step === 'finish' && (
                    <div className="w-[700px] bg-slate-900 border border-slate-700/50 rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden relative">
                        {/* Background Video Layer */}
                        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
                            <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-20">
                                <source src="/videos/heros-bg-looped.mp4" type="video/mp4" />
                            </video>
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
                        </div>
                    {error && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-500/10 border border-red-500/30 text-red-500 px-4 py-2 rounded-lg text-sm flex items-center gap-2 z-50 animate-in slide-in-from-top-4">
                            <AlertTriangle size={14} />
                            {error}
                            <button onClick={() => setError('')} className="ml-2 hover:bg-red-500/20 p-1 rounded"><X size={14} /></button>
                        </div>
                    )}
                    <div className="p-12 text-center relative z-10 border-b border-slate-700/30">
                        <div className="w-16 h-16 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/20 shadow-[0_0_30px_-5px_rgba(16,185,129,0.3)]">
                            <Check size={32} />
                        </div>
                        <h2 className="text-3xl font-black text-white mb-2 tracking-tight uppercase italic">Welcome to StratSync</h2>
                        <p className="text-sm text-slate-300 font-medium">Please review your setup before finalizing. Everything will be imported into your hub.</p>
                    </div>
                    <div className="p-8 lg:p-10 bg-slate-800/40 space-y-4 relative z-10">
                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                            <div className="flex items-center gap-3">
                                <Trophy size={18} className="text-orange-500" />
                                <div>
                                    <div className="font-bold text-slate-200">Leagues to Import</div>
                                    <div className="text-xs text-slate-500">Faceit and ESEA Leagues</div>
                                </div>
                            </div>
                            <div className="font-mono font-bold text-lg text-white bg-slate-900 px-3 py-1 rounded-lg border border-slate-700">
                                {importQueue.length}
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                            <div className="flex items-center gap-3">
                                <Users size={18} className="text-blue-500" />
                                <div>
                                    <div className="font-bold text-slate-200">Players to Roster</div>
                                    <div className="text-xs text-slate-500">Linked Faceit Profiles</div>
                                </div>
                            </div>
                            <div className="font-mono font-bold text-lg text-white bg-slate-900 px-3 py-1 rounded-lg border border-slate-700">
                                {rosterToImport.length}
                            </div>
                        </div>
                    </div>
                    <div className="p-6 bg-slate-950 border-t border-slate-700/30 flex items-center justify-between relative z-10">
                        <button 
                            className="px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                            onClick={() => setStep('serverRegion')}
                            disabled={loading}
                        >
                            Go Back
                        </button>
                        <button 
                            className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-2.5 rounded-lg flex items-center gap-2 font-bold shadow-[0_0_15px_rgba(16,185,129,0.3)] transition-all disabled:opacity-50"
                            onClick={handleCompleteAll}
                            disabled={loading}
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            Complete Setup
                        </button>
                    </div>
                </div>
                )}

                {step === 'league' && (
                    <div className="team-setup-card border-none shadow-[0_0_80px_rgba(0,0,0,0.8)]">
                <div className="team-setup-header">
                    <h2 className="team-setup-title">
                        Initial League Setup
                    </h2>
                    <p className="team-setup-subtitle">
                        Does your team have an active season? Import it now or create one manually.
                    </p>
                </div>

                <div className="team-setup-content">
                    <div className="league-setup-container animate-fade-in" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                            {/* Left Pane: Faceit Import */}
                            <div className="league-setup-pane">
                                <div className="league-pane-title">
                                    <img src="/icons/socials/Icon_Faceit.png" alt="Faceit" className="w-5 h-5 object-contain" />
                                    Import Championship
                                </div>
                                <p className="league-pane-desc">
                                    Link your team's active Faceit league/season URL. We will automatically fetch all matches, schedules, and standings.
                                </p>
                                <div className="flex flex-col gap-3 mt-auto">
                                    <input 
                                        type="text" 
                                        placeholder="https://www.faceit.com/en/championship/..."
                                        className="setup-input"
                                        value={faceitUrl}
                                        onChange={e => {
                                            setFaceitUrl(e.target.value);
                                            setFetchedLeague(null);
                                        }}
                                        disabled={loading || !!fetchedLeague}
                                    />
                                    {fetchedLeague && (
                                        <div className="bg-slate-900/80 border border-slate-700 rounded-xl p-4 flex items-center gap-4 animate-in fade-in slide-in-from-top-2 relative group border-orange-500/30 mt-2 mr-2 mb-2">
                                            <button 
                                                onClick={handleClearFaceitFetch}
                                                className="absolute top-2 right-2 bg-slate-800 text-slate-400 border border-slate-600 hover:bg-red-500 hover:text-white rounded-full p-1 transition-colors z-10"
                                            >
                                                <X size={14} />
                                            </button>
                                            <div className="w-12 h-12 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0 overflow-hidden">
                                                {fetchedLeague.avatar ? (
                                                    <img src={fetchedLeague.avatar} alt="Logo" className="w-full h-full object-cover" />
                                                ) : (
                                                    <Trophy size={20} className="text-slate-500" />
                                                )}
                                            </div>
                                            <div>
                                                <div className="text-[10px] font-black text-orange-500 uppercase tracking-widest mb-0.5">Preview</div>
                                                <div className="font-bold text-white text-sm line-clamp-2 leading-tight">{fetchedLeague.name}</div>
                                            </div>
                                        </div>
                                    )}
                                    <button 
                                        className="setup-btn setup-btn-orange"
                                        onClick={fetchedLeague ? handleAddFaceitToQueue : handleFetchFaceit}
                                        disabled={loading || !faceitUrl.trim()}
                                    >
                                        {loading ? <Loader2 size={16} className="animate-spin" /> : fetchedLeague ? <ChevronRight size={16} /> : <img src="/icons/socials/Icon_Faceit.png" alt="Faceit" className="w-4 h-4 object-contain" />}
                                        {fetchedLeague ? 'Add to List' : 'Fetch Preview'}
                                    </button>
                                </div>
                            </div>

                            {/* Middle Pane: Removed/Archived Leagues */}
                            <div className="league-setup-pane relative">
                                <div className="absolute inset-y-0 -left-[20px] w-[1px] bg-slate-700 hidden lg:block" />
                                
                                <div className="flex items-center justify-between">
                                    <div className="league-pane-title text-slate-400 m-0">
                                        <FileEdit size={18} className="text-slate-500" />
                                        Archived Imports
                                    </div>
                                    <div className="flex bg-slate-900/80 rounded-lg p-0.5 border border-slate-800 shrink-0">
                                        <button 
                                            onClick={() => setArchivedTab('active')}
                                            className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-md transition-colors ${archivedTab === 'active' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-400'}`}
                                        >
                                            Active
                                        </button>
                                        <button 
                                            onClick={() => setArchivedTab('history')}
                                            className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-md transition-colors ${archivedTab === 'history' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-400'}`}
                                        >
                                            History
                                        </button>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 flex-1 mt-2">
                                    <div className="url-list-container custom-scrollbar">
                                        {removedQueue.filter(item => archivedTab === 'active' ? !item.isClosed : item.isClosed).length === 0 ? (
                                            <div className="text-center py-6 text-slate-600 text-sm italic">
                                                No archived leagues found.
                                            </div>
                                        ) : (
                                            removedQueue.map((item, originalIndex) => ({ item, originalIndex }))
                                                .filter(({ item }) => archivedTab === 'active' ? !item.isClosed : item.isClosed)
                                                .map(({ item, originalIndex }) => {
                                                const borderColor = item.type === 'esea' ? 'border-emerald-500/20' : item.type === 'dach' ? 'border-amber-500/20' : 'border-slate-500/20';
                                                const bgColor = 'bg-slate-900/50';
                                                
                                                let avatarSrc: string | null = null;
                                                if (item.type === 'faceit') avatarSrc = item.avatar;
                                                else if (item.type === 'dach') avatarSrc = '/images/Dach_Master_CS_Logo.png';
                                                
                                                let label = '';
                                                if (item.type === 'faceit') label = 'Championship';
                                                else if (item.type === 'esea') label = `ESEA · ${item.division} ${item.region}`;
                                                else if (item.type === 'dach') label = `DACH Master${item.division && item.division !== 'Division' && item.division !== 'Unknown' ? ' · ' + item.division : ''}${item.group ? ' · ' + item.group : ''}`;
                                                
                                                return (
                                                    <div key={`rem-${originalIndex}`} className={`p-3 mr-2 mb-2 rounded-lg border ${borderColor} ${bgColor} relative group animate-in fade-in flex-shrink-0 overflow-hidden opacity-70 hover:opacity-100 transition-opacity`}>
                                                        {item.type === 'dach' && avatarSrc && (
                                                            <>
                                                                <div className="absolute inset-0 z-0 opacity-10 pointer-events-none grayscale" style={{ backgroundImage: `url(${avatarSrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                                                                <div className="absolute inset-0 z-0 bg-slate-950/80 pointer-events-none" />
                                                            </>
                                                        )}
                                                        
                                                        <button 
                                                            onClick={() => handleRestoreFromRemoved(originalIndex)}
                                                            className="absolute top-2 right-2 bg-emerald-600/80 border border-emerald-500 text-white hover:bg-emerald-500 rounded-lg p-1.5 transition-colors z-20 shadow-lg shadow-emerald-500/20"
                                                            title="Add back to Import List"
                                                        >
                                                            <ArrowRight size={14} />
                                                        </button>
                                                        <div className="relative z-10 flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-lg shrink-0 border bg-slate-900 border-slate-700 overflow-hidden flex items-center justify-center grayscale">
                                                                {item.type === 'faceit' && avatarSrc ? (
                                                                    <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
                                                                ) : item.type === 'esea' ? (
                                                                    <img src="/icons/socials/Esea_Icon.png" alt="ESEA" className="w-5 h-5 object-contain" />
                                                                ) : item.type === 'dach' ? (
                                                                    <img src="/images/Dach_Master_CS_Logo_2.png" alt="DACH" className="w-full h-full object-contain" />
                                                                ) : (
                                                                    <img src="/icons/socials/Icon_Faceit.png" alt="Faceit" className="w-5 h-5 object-contain" />
                                                                )}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="text-[9px] text-slate-500 font-black uppercase tracking-widest leading-none mb-1">
                                                                    {label}
                                                                </div>
                                                                <div className="font-bold text-slate-300 text-xs truncate leading-tight">{item.name}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Pane: Import List */}
                            <div className="league-setup-pane relative">
                                <div className="absolute inset-y-0 -left-[20px] w-[1px] bg-slate-700 hidden lg:block" />
                                
                                <div className="league-pane-title">
                                    <ListChecks size={18} className="text-blue-500" />
                                    Import List
                                </div>
                                <p className="league-pane-desc">
                                    {importQueue.some(q => 'autoDetected' in q && q.autoDetected)
                                        ? 'We found your team in our league database! These divisions will be imported automatically.'
                                        : 'The following leagues will be imported for your team.'}
                                </p>
                                <div className="flex flex-col gap-3 flex-1 overflow-hidden">
                                    <div className="url-list-container custom-scrollbar mt-2 pr-2" style={{ maxHeight: '100%', overflowY: 'auto' }}>
                                        {importQueue.length === 0 ? (
                                            <div className="text-center py-6 text-slate-500 text-sm italic">
                                                No leagues detected yet. Add one via Faceit URL.
                                            </div>
                                        ) : (
                                            <>
                                                {/* Active Leagues */}
                                                {importQueue.filter(i => !i.isClosed).map((item, idx) => {
                                                    const originalIndex = importQueue.findIndex(q => q === item);
                                                    const isAuto = 'autoDetected' in item && item.autoDetected;
                                                    const borderColor = item.type === 'esea' ? 'border-emerald-500/30' : item.type === 'dach' ? 'border-amber-500/30' : 'border-blue-500/30';
                                                    const bgColor = item.type === 'esea' ? 'bg-emerald-500/5' : item.type === 'dach' ? 'bg-amber-500/5' : 'bg-blue-500/5';
                                                    const labelColor = item.type === 'esea' ? 'text-emerald-400/90' : item.type === 'dach' ? 'text-amber-400/90' : 'text-blue-400/90';
                                                    
                                                    let avatarSrc: string | null = null;
                                                    if (item.type === 'faceit') avatarSrc = item.avatar;
                                                    else if (item.type === 'dach') avatarSrc = '/images/Dach_Master_CS_Logo.png';
                                                    
                                                    let label = '';
                                                    if (item.type === 'faceit') label = 'Championship';
                                                    else if (item.type === 'esea') label = `ESEA · ${item.division} ${item.region}`;
                                                    else if (item.type === 'dach') label = `DACH Master${item.division && item.division !== 'Division' && item.division !== 'Unknown' ? ' · ' + item.division : ''}${item.group ? ' · ' + item.group : ''}`;
                                                    
                                                    return (
                                                        <div key={`act-${originalIndex}`} className={`p-3 mt-2 mb-2 rounded-lg border ${borderColor} ${bgColor} relative group animate-in fade-in flex-shrink-0 overflow-hidden`}>
                                                            {item.type === 'dach' && avatarSrc && (
                                                                <>
                                                                    <div className="absolute inset-0 z-0 opacity-20 pointer-events-none transition-opacity group-hover:opacity-30 mix-blend-lighten" style={{ backgroundImage: `url(${avatarSrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                                                                    <div className="absolute inset-0 z-0 bg-gradient-to-r from-slate-900/90 via-slate-900/60 to-slate-900/80 pointer-events-none" />
                                                                </>
                                                            )}
                                                            
                                                            <button 
                                                                onClick={() => handleRemoveFromQueue(originalIndex)}
                                                                className="absolute top-2 right-2 bg-slate-800 border border-slate-600 text-slate-400 hover:bg-red-500 hover:text-white rounded-full p-1 transition-colors z-20"
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                            <div className="relative z-10 flex items-center gap-3">
                                                                <div className={`w-10 h-10 rounded-lg shrink-0 border overflow-hidden flex items-center justify-center ${item.type === 'faceit' && avatarSrc ? 'bg-slate-900 border-slate-700' : item.type === 'esea' ? 'bg-emerald-600 border-emerald-500 shadow-inner' : 'bg-slate-900 border-slate-700'}`}>
                                                                    {item.type === 'faceit' && avatarSrc ? (
                                                                        <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
                                                                    ) : item.type === 'esea' ? (
                                                                        <img src="/icons/socials/Esea_Icon.png" alt="ESEA" className="w-5 h-5 object-contain" />
                                                                    ) : item.type === 'dach' ? (
                                                                        <img src="/images/Dach_Master_CS_Logo_2.png" alt="DACH" className="w-full h-full object-contain" />
                                                                    ) : (
                                                                        <img src="/icons/socials/Icon_Faceit.png" alt="Faceit" className="w-5 h-5 object-contain" />
                                                                    )}
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <div className={`text-[9px] ${labelColor} font-black uppercase tracking-widest leading-none mb-1`}>{label}</div>
                                                                    <div className="font-bold text-white text-xs truncate leading-tight">{item.name}</div>
                                                                </div>
                                                                {isAuto && (
                                                                    <div className="shrink-0 flex items-center gap-1 text-[9px] text-slate-500 font-bold uppercase tracking-wider bg-slate-800/80 backdrop-blur-sm px-2 py-1 rounded border border-slate-700">
                                                                        <Check size={10} />
                                                                        Auto
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Completed Leagues */}
                                                {importQueue.filter(i => i.isClosed).length > 0 && (
                                                    <div className="mt-4 border-t border-slate-800 pt-3">
                                                        <button 
                                                            onClick={() => setShowCompleted(!showCompleted)}
                                                            className="flex items-center gap-2 w-full text-left p-2 rounded-lg hover:bg-slate-800/50 transition-colors group"
                                                        >
                                                            <ChevronRight size={16} className={`text-slate-500 transition-transform ${showCompleted ? 'rotate-90' : ''}`} />
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 group-hover:text-slate-400">
                                                                Completed / Closed ({importQueue.filter(i => i.isClosed).length})
                                                            </span>
                                                        </button>
                                                        
                                                        {showCompleted && (
                                                            <div className="mt-2 pl-2 border-l-2 border-slate-800 animate-in fade-in slide-in-from-top-2">
                                                                {importQueue.filter(i => i.isClosed).map((item, idx) => {
                                                                    const originalIndex = importQueue.findIndex(q => q === item);
                                                                    const borderColor = item.type === 'esea' ? 'border-emerald-500/20' : item.type === 'dach' ? 'border-amber-500/20' : 'border-blue-500/20';
                                                                    const bgColor = 'bg-slate-900/40';
                                                                    const labelColor = 'text-slate-500';
                                                                    
                                                                    let avatarSrc: string | null = null;
                                                                    if (item.type === 'faceit') avatarSrc = item.avatar;
                                                                    else if (item.type === 'dach') avatarSrc = '/images/Dach_Master_CS_Logo.png';
                                                                    
                                                                    let label = '';
                                                                    if (item.type === 'faceit') label = 'Championship';
                                                                    else if (item.type === 'esea') label = `ESEA · ${item.division} ${item.region}`;
                                                                    else if (item.type === 'dach') label = `DACH Master${item.division && item.division !== 'Division' && item.division !== 'Unknown' ? ' · ' + item.division : ''}${item.group ? ' · ' + item.group : ''}`;
                                                                    
                                                                    return (
                                                                        <div key={`cmp-${originalIndex}`} className={`p-3 mt-2 mb-2 rounded-lg border ${borderColor} ${bgColor} relative group animate-in fade-in flex-shrink-0 overflow-hidden opacity-80 hover:opacity-100 transition-opacity`}>
                                                                            {item.type === 'dach' && avatarSrc && (
                                                                                <>
                                                                                    <div className="absolute inset-0 z-0 opacity-10 pointer-events-none grayscale" style={{ backgroundImage: `url(${avatarSrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                                                                                    <div className="absolute inset-0 z-0 bg-slate-950/80 pointer-events-none" />
                                                                                </>
                                                                            )}
                                                                            
                                                                            {/* Always show X button for completed leagues, even if autoDetected */}
                                                                            <button 
                                                                                onClick={() => handleRemoveFromQueue(originalIndex)}
                                                                                className="absolute top-2 right-2 bg-slate-800 border border-slate-600 text-slate-400 hover:bg-red-500 hover:text-white rounded-full p-1 transition-colors z-20"
                                                                            >
                                                                                <X size={12} />
                                                                            </button>

                                                                            <div className="relative z-10 flex items-center gap-3">
                                                                                <div className={`w-10 h-10 rounded-lg shrink-0 border bg-slate-900 border-slate-700 overflow-hidden flex items-center justify-center grayscale`}>
                                                                                    {item.type === 'faceit' && avatarSrc ? (
                                                                                        <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
                                                                                    ) : item.type === 'esea' ? (
                                                                                        <img src="/icons/socials/Esea_Icon.png" alt="ESEA" className="w-5 h-5 object-contain" />
                                                                                    ) : item.type === 'dach' ? (
                                                                                        <img src="/images/Dach_Master_CS_Logo_2.png" alt="DACH" className="w-full h-full object-contain" />
                                                                                    ) : (
                                                                                        <img src="/icons/socials/Icon_Faceit.png" alt="Faceit" className="w-5 h-5 object-contain" />
                                                                                    )}
                                                                                </div>
                                                                                <div className="min-w-0 flex-1">
                                                                                    <div className={`text-[9px] ${labelColor} font-black uppercase tracking-widest leading-none mb-1`}>{label}</div>
                                                                                    <div className="font-bold text-slate-300 text-xs truncate leading-tight">{item.name}</div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                </div>

                {error && (
                    <div className="px-10 pb-4 animate-in fade-in">
                        <div className="bg-red-500/10 border border-red-500/30 text-red-500 px-4 py-3 rounded-xl text-xs font-bold flex items-center justify-between gap-4">
                            <span className="flex items-center gap-2">
                                <AlertTriangle size={14} /> {error}
                            </span>
                            <button onClick={() => setError('')} className="text-red-500/50 hover:text-red-500 transition-colors p-1 -mr-1">
                                <X size={14} />
                            </button>
                        </div>
                    </div>
                )}

                <div className="team-setup-footer league-setup-footer">
                    <button 
                        className="setup-btn setup-btn-emerald league-import-button"
                        onClick={handleContinueToRoster}
                        disabled={loading || importQueue.length === 0}
                    >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Import {importQueue.length} League{importQueue.length !== 1 ? 's' : ''}
                    </button>
                    {importQueue.length === 0 && (
                        <div className="league-skip-action animate-in fade-in transition-all">
                            <button className="setup-btn team-wizard-skip" onClick={handleContinueToRoster}>
                                Skip League Setup
                            </button>
                        </div>
                    )}
                </div>
                </div>
                )}
            </div>
        </div>
    );
}
